/**
 * The cross-tree switch this tab is in the middle of, if any (DV-3421).
 *
 * <p>A cross-tree switch re-anchors the shared token well before the slice
 * moves (`scopeChanged` is dispatched at the very end), so for a while the
 * slice names one tree and the token another. Rounds 2–4 of the review each
 * found a request in that window carrying the wrong header, at a different
 * call site each time, and rounds 5–7 tried to pick the right one per request
 * by judging the token it carried. The review of `b5251a34` showed that any
 * rule which sends the TARGET inside the window is wrong: the old account's
 * page stays mounted until the commit, and a create it fires in that window
 * (a poll's follow-up, an upload confirm, a form saved after the header tree
 * was clicked) takes its owner from the selection and lands in the target.
 *
 * <p>So the window has no say over the header at all. Every request keeps
 * sending the account the UI is showing — the OLD selection — and once the
 * token has moved the guard refuses it with a 403, which is the safe failure
 * (`refusedForKnownUnreachableSelection` surfaces it without a refresh). The
 * switch's own calls are all `tokenOnly` and carry no selection, so nothing
 * the switch needs is lost.
 *
 * <p>What the window still does: it tells this tab's reconciler that its own
 * stamp disagreeing with its own token is expected for now — and only that; a
 * different user taking over, or a logout, is still acted on mid-switch.
 *
 * <p>A module-level value rather than slice state: the header reader runs on
 * the request path and the reconciler from plain listeners, so both need it
 * with no store and no async step. A leaf module, so neither imports the other.
 */

/** Identifies the switch that began the current window; only it may end it. */
let pending: symbol | null = null;

/** A reconcile trigger arrived while the window was open and was stood down. */
let triggerDropped = false;

/** Proof of ownership, returned by {@link beginSwitch} and required by {@link endSwitch}. */
export type SwitchHandle = symbol;

/**
 * Enter the window. Replaces any switch already in it: the newer choice is the
 * one the user is waiting on, and the older one's {@link endSwitch} then
 * becomes a no-op rather than clearing the newer one's state.
 */
export const beginSwitch = (): SwitchHandle => {
  const owner = Symbol('switch');
  pending = owner;
  return owner;
};

/**
 * Leave it — only if `handle` still owns it. Call from a `finally` that also
 * covers the `scopeChanged` dispatch: the slice is what takes over sending the
 * header, so ending any earlier re-opens the gap, and not ending at all on a
 * failure would leave every later request scoped to an account the UI is not
 * showing.
 *
 * <p>Returns true when a reconcile trigger was stood down during the window
 * ({@link noteTriggerDropped}); the caller replays it (DV-3421 review round 6,
 * item B). A focused tab gets no further focus, visibility or storage event,
 * so a dropped trigger is otherwise lost for good.
 */
export const endSwitch = (handle: SwitchHandle): boolean => {
  if (pending !== handle) return false;
  pending = null;
  const replay = triggerDropped;
  triggerDropped = false;
  return replay;
};

/** True while this tab is mid cross-tree switch. */
export const isSwitching = (): boolean => pending !== null;

/** `reconcileScope` stood down for the window; remember to replay it. */
export const noteTriggerDropped = (): void => {
  if (pending !== null) triggerDropped = true;
};
